import { useState } from 'react';

interface DownloadState {
  isOpen: boolean;
  status: 'idle' | 'in_progress' | 'success' | 'error';
  permitNo?: string;
  errorMessage?: string;
  message?: string;
}

interface PdfDownloaderProps {
  getPermitDownloadUids: (permitNo: string) => Promise<any>;
  generateReportsPDFByUuid: (params: { uuid: string; permitNo: string }) => Promise<any>;
}

export const usePdfDownloader = ({ getPermitDownloadUids, generateReportsPDFByUuid }: PdfDownloaderProps) => {
  const [downloadState, setDownloadState] = useState<DownloadState>({
    isOpen: false,
    status: 'idle',
  });

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadInitiated, setDownloadInitiated] = useState(false);

  const handleDownload = async (permitNo: string) => {
    if (isDownloading || downloadInitiated) return;
    
    setIsDownloading(true);
    setDownloadInitiated(true);
    setDownloadState({
      isOpen: true,
      status: 'in_progress',
      permitNo,
      message: 'Starting PDF generation...'
    });

    try {
      // Step 1: Get UUID for the permit
      const uuidResponse = await getPermitDownloadUids(permitNo);
      
      if (!uuidResponse?.data?.uuid) {
        throw new Error('Failed to start PDF generation - no UUID received');
      }

      // Step 2: Poll for PDF generation status
      let attempts = 0;
      const maxAttempts = 30;
      //@ts-ignore
      let result;

      while (attempts < maxAttempts) {
        result = await generateReportsPDFByUuid({
          uuid: uuidResponse.data.uuid,
          permitNo
        })
        //@ts-ignore
        .unwrap();

        setDownloadState(prev => ({
          ...prev,
          //@ts-ignore
          status: result.status,
          //@ts-ignore
          message: result.message
        }));

        if (result.status === 'success' && result.signed_url) {
          // Create a hidden anchor tag to trigger download
          const link = document.createElement('a');
          link.href = result.signed_url;
          link.download = `${permitNo}.pdf`; // Set the filename
          link.style.display = 'none';
          
          // Append to body and click programmatically
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            // Revoke the object URL to free memory
            URL.revokeObjectURL(link.href);
          }, 100);
          
          setDownloadState(prev => ({
            ...prev,
            status: 'success',
            message: 'PDF download started'
          }));
          
          setTimeout(() => {
            setDownloadState(prev => ({
              ...prev,
              isOpen: false,
              status: 'idle'
            }));
          }, 2000);
          return;
        }
        else if (result.status === 'error') {
          throw new Error(result.message || 'PDF generation failed');
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      }

      throw new Error('PDF generation timed out. Please try again later.');
    } catch (error) {
      setDownloadState(prev => ({
        ...prev,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred'
      }));
    } finally {
      setIsDownloading(false);
      setTimeout(() => setDownloadInitiated(false), 3000); // Reset after cooldown
    }
  };

  return {
    downloadState,
    setDownloadState,
    handleDownload,
    isDownloading
  };
};